import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Activity, UserPlus, UserMinus, GraduationCap, ShieldAlert, Play, ArrowLeft } from "lucide-react";

const ICON_MAP = {
  activity: Activity,
  "user-plus": UserPlus,
  "user-minus": UserMinus,
  "graduation-cap": GraduationCap,
  "shield-alert": ShieldAlert,
};

function ScenarioCard({ def, onStart }) {
  const Icon = ICON_MAP[def.icon] || Activity;
  return (
    <div
      data-testid={`po-scenario-${def.slug}`}
      className="rounded-3xl border border-brand-linen bg-white p-6 hover:border-brand-ink/40 hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-full bg-brand-linen p-2.5 text-brand-ink">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand-muted">{def.category}</div>
          <h3 className="font-serif text-xl text-brand-ink leading-tight mt-0.5">{def.name}</h3>
        </div>
      </div>
      <p className="text-sm text-brand-body leading-relaxed mb-5">{def.description}</p>
      <button
        onClick={() => onStart(def)}
        data-testid={`po-start-${def.slug}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-brand-bone px-4 py-2 text-sm hover:bg-brand-inkHover transition-colors"
      >
        <Play className="w-3.5 h-3.5" strokeWidth={1.5} />
        Start
      </button>
    </div>
  );
}

export default function PracticeOpsLibrary() {
  const [defs, setDefs] = useState(null);
  const [err, setErr] = useState("");
  const [startFor, setStartFor] = useState(null);
  const [eventDate, setEventDate] = useState(new Date().toISOString().slice(0, 10));
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/practice-ops/definitions");
        setDefs(res.data || []);
      } catch (e) {
        setErr(e.response?.data?.detail || "Could not load scenarios");
      }
    })();
  }, []);

  const beginStart = (def) => {
    setStartFor(def);
    setTitle("");
    setEventDate(new Date().toISOString().slice(0, 10));
  };

  const confirmStart = async () => {
    if (!startFor) return;
    setBusy(true);
    try {
      const res = await api.post("/practice-ops/instances", {
        slug: startFor.slug,
        title: title.trim() || null,
        event_date: eventDate || null,
      });
      toast.success("Workflow started");
      navigate(`/practice-ops/instances/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start workflow");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout wide>
      <div className="mb-6">
        <Link to="/practice-ops" data-testid="po-library-back"
          className="inline-flex items-center gap-1.5 text-sm text-brand-secondary hover:text-brand-ink transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          Back to Practice Ops
        </Link>
      </div>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1">Scenario library</div>
        <h1 className="font-serif text-4xl text-brand-ink">Start an operational workflow</h1>
        <p className="text-brand-body text-sm mt-2 max-w-2xl">
          Choose the operational situation you're dealing with. Every workflow is deterministic and
          rules-based — no AI, no patient data.
        </p>
      </div>

      {err && <div data-testid="po-library-error" className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>}

      {defs === null ? (
        <div className="text-brand-muted text-sm">Loading…</div>
      ) : defs.length === 0 ? (
        <div className="text-brand-muted text-sm">No scenarios available.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {defs.map((d) => <ScenarioCard key={d.slug} def={d} onStart={beginStart} />)}
        </div>
      )}

      {startFor && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="po-start-modal"
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !busy && setStartFor(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1">Start workflow</div>
            <h2 className="font-serif text-2xl text-brand-ink mb-4">{startFor.name}</h2>
            <div className="mb-4">
              <label className="text-xs uppercase tracking-widest text-brand-muted">Reference / title (optional)</label>
              <input
                data-testid="po-start-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Reception cover — 12 Feb"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm"
              />
              <div className="mt-2 text-[11px] text-brand-muted">
                Do not enter patient-identifiable or patient-related information.
              </div>
            </div>
            <div className="mb-6">
              <label className="text-xs uppercase tracking-widest text-brand-muted">
                {(startFor.questions || []).find((q) => q.type === "date")?.label || "Event date"}
              </label>
              <input
                data-testid="po-start-event-date"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-brand-linen bg-white focus:outline-none focus:border-brand-ink text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button disabled={busy} onClick={() => setStartFor(null)}
                className="px-4 py-2 rounded-full border border-brand-linen text-sm text-brand-secondary hover:text-brand-ink transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button disabled={busy} onClick={confirmStart} data-testid="po-start-confirm"
                className="px-4 py-2 rounded-full bg-brand-ink text-brand-bone text-sm hover:bg-brand-inkHover transition-colors disabled:opacity-50">
                {busy ? "Starting…" : "Start workflow"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
